import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  getMqttConnections,
  createMqttConnection,
  updateMqttConnection,
  deleteMqttConnection,
  toggleMqttConnection,
  getRecentExternalMessages,
  clearExternalMessages,
  publishMessage,
  createDevice,
  getWebSocketUrl,
} from '../services/api';
import { copyToClipboard } from '../utils/copyToClipboard';

interface MqttConn {
  id: string;
  name: string;
  host: string;
  port: number;
  username: string | null;
  topics: string;
  enabled: boolean;
  created_at: string;
}

interface ExtMessage {
  connection_id: string;
  connection_name: string;
  topic: string;
  payload: string;
  received_at: string;
}

interface FieldMapping {
  rowId: string;
  field: string;
  label: string;
  path: string;
  scale: string;
  offset: string;
  unit: string;
  type: 'number' | 'string';
}

type MqttIconName =
  | 'bridge'
  | 'plug'
  | 'pulse'
  | 'map'
  | 'send'
  | 'plus'
  | 'pause'
  | 'play'
  | 'filter'
  | 'copy'
  | 'clock'
  | 'calendar'
  | 'lock'
  | 'database'
  | 'search';

type MqttFlowAccent = 'green' | 'blue' | 'amber' | 'violet';

const emptyForm = { name: '', host: '', port: '1883', username: '', password: '', topics: '#' };
let mappingRowCounter = 0;

function parseTopics(topics: string): string[] {
  return topics.split(',').map((topic) => topic.trim()).filter(Boolean);
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function getTopicRoot(topic: string) {
  return topic.split('/')[0] || '(empty)';
}

function createMappingRowId(prefix = 'mapping') {
  mappingRowCounter += 1;
  return `${prefix}-${mappingRowCounter}`;
}

function normalizeEditableNumber(value: string, fallback: number) {
  if (value.trim() === '') return fallback;
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function createDefaultMapping(overrides: Partial<Omit<FieldMapping, 'rowId'>> = {}): FieldMapping {
  return {
    rowId: createMappingRowId('mqtt-mapping'),
    field: 'temperature',
    label: 'Temperature',
    path: '',
    scale: '1',
    offset: '0',
    unit: '°C',
    type: 'number',
    ...overrides,
  };
}

function tryFormatJson(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

function resolvePath(obj: any, path: string): any {
  if (!path || path === '$') return obj;
  const clean = path.startsWith('$.') ? path.slice(2) : path.startsWith('$') ? path.slice(1) : path;
  const parts = clean.split(/\.|\[(\d+)\]/).filter(Boolean);
  let val = obj;
  for (const part of parts) {
    if (val == null) return undefined;
    val = isNaN(+part) ? val[part] : val[+part];
  }
  return val;
}

function parsePreview(payload: string, mappings: FieldMapping[]): Record<string, number | string> | null {
  let data: any;
  try {
    data = JSON.parse(payload);
  } catch {
    return null;
  }

  const result: Record<string, number | string> = {};
  for (const mapping of mappings) {
    if (!mapping.field || !mapping.path) continue;
    const raw = resolvePath(data, mapping.path);
    if (raw == null) continue;

    if (mapping.type === 'string') {
      result[mapping.field] = typeof raw === 'boolean' ? (raw ? 'ON' : 'OFF') : String(raw);
      continue;
    }

    const num = typeof raw === 'number' ? raw : parseFloat(raw);
    if (isNaN(num)) continue;
    result[mapping.field] =
      num * normalizeEditableNumber(mapping.scale, 1) + normalizeEditableNumber(mapping.offset, 0);
  }

  return Object.keys(result).length > 0 ? result : null;
}

export default function MqttConnectionsPage() {
  const [connections, setConnections] = useState<MqttConn[]>([]);
  const [messages, setMessages] = useState<ExtMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [showPassword, setShowPassword] = useState(false);
  const [detailMsg, setDetailMsg] = useState<ExtMessage | null>(null);

  const [paused, setPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [filterConn, setFilterConn] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('list');
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const [showVirtualModal, setShowVirtualModal] = useState(false);
  const [vdName, setVdName] = useState('');
  const [vdConn, setVdConn] = useState('');
  const [vdTopicPattern, setVdTopicPattern] = useState('');
  const [vdMappings, setVdMappings] = useState<FieldMapping[]>(() => [createDefaultMapping()]);
  const [vdTestPayload, setVdTestPayload] = useState('');
  const [vdPreview, setVdPreview] = useState<Record<string, number | string> | null>(null);

  const [pubConn, setPubConn] = useState('');
  const [pubTopic, setPubTopic] = useState('');
  const [pubPayload, setPubPayload] = useState('');
  const [pubFormat, setPubFormat] = useState<'plaintext' | 'json' | 'hex' | 'base64'>('plaintext');
  const [pubQos, setPubQos] = useState(0);
  const [pubRetain, setPubRetain] = useState(false);
  const [pubSending, setPubSending] = useState(false);
  const [pubSent, setPubSent] = useState(false);

  const msgListRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pendingRef = useRef<ExtMessage[]>([]);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  const load = useCallback(async () => {
    try {
      const [conns, recent] = await Promise.all([getMqttConnections(), getRecentExternalMessages()]);
      setConnections(conns);
      if (!paused) setMessages(recent);
    } finally {
      setLoading(false);
    }
  }, [paused]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    const url = getWebSocketUrl('/ws');
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'external_mqtt_message') {
          if (paused) {
            pendingRef.current.unshift(msg.data);
            if (pendingRef.current.length > 200) pendingRef.current.pop();
          } else {
            setMessages((prev) => [msg.data, ...prev].slice(0, 500));
          }
        }
      } catch {}
    };
    return () => ws.close();
  }, [paused]);

  useEffect(() => {
    if (autoScroll && msgListRef.current && !paused) msgListRef.current.scrollTop = 0;
  }, [messages, autoScroll, paused]);

  useEffect(() => {
    if (!paused && pendingRef.current.length > 0) {
      setMessages((prev) => {
        const merged = [...pendingRef.current, ...prev].slice(0, 500);
        pendingRef.current = [];
        return merged;
      });
    }
  }, [paused]);

  const filteredMessages = useMemo(() => {
    let list = messages;
    if (filterConn !== 'all') list = list.filter((message) => message.connection_id === filterConn);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      list = list.filter(
        (message) =>
          message.topic.toLowerCase().includes(q) ||
          message.payload.toLowerCase().includes(q) ||
          message.connection_name.toLowerCase().includes(q)
      );
    }
    return list;
  }, [messages, filterConn, searchText]);

  const topicTree = useMemo(() => {
    const tree: Record<string, ExtMessage[]> = {};
    for (const message of filteredMessages) {
      const root = message.topic.split('/')[0] || '(empty)';
      if (!tree[root]) tree[root] = [];
      tree[root].push(message);
    }
    return tree;
  }, [filteredMessages]);

  const enabledConnectionOptions = useMemo(
    () => connections.filter((connection) => connection.enabled),
    [connections]
  );
  const enabledCount = enabledConnectionOptions.length;
  const subscriptionCount = useMemo(
    () =>
      connections.reduce(
        (total, connection) =>
          total + parseTopics(connection.topics).length,
        0
      ),
    [connections]
  );
  const configuredTopicRootCount = useMemo(
    () => new Set(connections.flatMap((connection) => parseTopics(connection.topics).map(getTopicRoot))).size,
    [connections]
  );
  const liveTopicRootCount = useMemo(
    () => new Set(messages.map((message) => getTopicRoot(message.topic))).size,
    [messages]
  );
  const selectedConnection = filterConn === 'all' ? null : connections.find((connection) => connection.id === filterConn);
  const primaryConnection = selectedConnection || enabledConnectionOptions[0] || connections[0] || null;
  const primaryTopics = useMemo(
    () => (primaryConnection ? parseTopics(primaryConnection.topics) : []),
    [primaryConnection]
  );
  const connectionStats = useMemo(() => {
    const stats = new Map<string, { messageCount: number; lastMessage: ExtMessage | null }>();
    for (const connection of connections) {
      stats.set(connection.id, { messageCount: 0, lastMessage: null });
    }
    for (const message of messages) {
      const entry = stats.get(message.connection_id);
      if (!entry) continue;
      entry.messageCount += 1;
      if (!entry.lastMessage) entry.lastMessage = message;
    }
    return stats;
  }, [connections, messages]);
  const primaryStats = primaryConnection
    ? connectionStats.get(primaryConnection.id) || { messageCount: 0, lastMessage: null }
    : { messageCount: 0, lastMessage: null };
  const workflowSteps: Array<{
    accent: MqttFlowAccent;
    copy: string;
    icon: MqttIconName;
    number: string;
    status: string;
    title: string;
  }> = [
    {
      accent: 'green',
      copy: 'Securely connect an MQTT broker.',
      icon: 'plug',
      number: '1',
      status: enabledCount > 0 ? `${enabledCount} connected` : 'Add bridge',
      title: 'Connect Bridge',
    },
    {
      accent: 'blue',
      copy: 'Subscribe to topics and watch live payloads.',
      icon: 'pulse',
      number: '2',
      status: subscriptionCount > 0 ? pluralize(subscriptionCount, 'topic') : 'No topics',
      title: 'Watch Topics',
    },
    {
      accent: 'amber',
      copy: 'Map topic payloads to virtual devices.',
      icon: 'map',
      number: '3',
      status: configuredTopicRootCount > 0 ? `${configuredTopicRootCount} roots ready` : 'Waiting',
      title: 'Map Device',
    },
    {
      accent: 'violet',
      copy: 'Send commands and monitor responses.',
      icon: 'send',
      number: '4',
      status: enabledConnectionOptions.length > 0 ? 'Ready' : 'Needs bridge',
      title: 'Publish Command',
    },
  ];

  useEffect(() => {
    if (enabledConnectionOptions.length === 0) {
      if (pubConn) setPubConn('');
      return;
    }
    if (!pubConn || !enabledConnectionOptions.some((connection) => connection.id === pubConn)) {
      setPubConn(enabledConnectionOptions[0].id);
    }
  }, [enabledConnectionOptions, pubConn]);

  function openVirtualModal(preFillMsg?: ExtMessage, preferredConnectionId?: string) {
    setVdName('');
    setVdConn(preFillMsg?.connection_id || preferredConnectionId || primaryConnection?.id || connections[0]?.id || '');
    setVdTopicPattern(preFillMsg?.topic || '');
    setVdMappings([createDefaultMapping()]);
    setVdTestPayload(preFillMsg?.payload || '');
    setVdPreview(null);
    setShowVirtualModal(true);
  }

  function runPreview() {
    setVdPreview(parsePreview(vdTestPayload, vdMappings));
  }

  function updateMapping(index: number, patch: Partial<FieldMapping>) {
    setVdMappings((prev) =>
      prev.map((mapping, mappingIndex) =>
        mappingIndex === index ? { ...mapping, ...patch } : mapping
      )
    );
  }

  async function saveVirtualDevice() {
    if (!vdName || !vdConn || !vdTopicPattern) return;
    const parserConfig = {
      mappings: vdMappings.reduce((acc, mapping) => {
        if (mapping.field && mapping.path) {
          acc[mapping.field] = {
            path: mapping.path,
            scale: normalizeEditableNumber(mapping.scale, 1),
            offset: normalizeEditableNumber(mapping.offset, 0),
            unit: mapping.unit || undefined,
            label: mapping.label || undefined,
            type: mapping.type || 'number',
          };
        }
        return acc;
      }, {} as any),
    };

    try {
      await createDevice({
        name: vdName,
        mqtt_connection_id: vdConn,
        gateway_topic: vdTopicPattern,
        parser_type: 'json_path',
        parser_config: parserConfig,
        heartbeat_timeout_minutes: 5,
      });
      setShowVirtualModal(false);
    } catch (err: any) {
      alert(err.response?.data?.details || err.response?.data?.error || 'Virtual device creation failed.');
    }
  }

  async function handlePublish(event: React.FormEvent) {
    event.preventDefault();
    if (!pubConn || !pubTopic) return;
    setPubSending(true);
    try {
      await publishMessage(pubConn, {
        topic: pubTopic,
        payload: pubPayload,
        format: pubFormat,
        qos: pubQos,
        retain: pubRetain,
      });
      setPubSent(true);
      setTimeout(() => setPubSent(false), 1500);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Publish failed.');
    } finally {
      setPubSending(false);
    }
  }

  function handleExport() {
    const blob = new Blob([JSON.stringify(filteredMessages, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mqtt-messages-${new Date().toISOString().slice(0, 19)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function handleClear() {
    if (!window.confirm('Clear all message history?')) return;
    await clearExternalMessages();
    setMessages([]);
    pendingRef.current = [];
  }

  async function copyPayload(text: string, index: number) {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopiedIdx(index);
      setTimeout(() => setCopiedIdx(null), 1200);
    }
  }

  function startEdit(connection: MqttConn) {
    setEditingId(connection.id);
    setForm({
      name: connection.name,
      host: connection.host,
      port: String(connection.port),
      username: connection.username || '',
      password: '',
      topics: connection.topics,
    });
    setShowPassword(false);
    setShowForm(true);
  }

  function startCreate() {
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowPassword(false);
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowPassword(false);
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const payload = {
      name: form.name,
      host: form.host,
      port: normalizeEditableNumber(form.port, 1883),
      username: form.username || undefined,
      password: form.password || undefined,
      topics: form.topics,
    };
    if (editingId) await updateMqttConnection(editingId, payload);
    else await createMqttConnection(payload);
    cancelForm();
    load();
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this connection?')) return;
    await deleteMqttConnection(id);
    load();
  }

  async function handleToggle(id: string, enabled: boolean) {
    await toggleMqttConnection(id, !enabled);
    load();
  }

  if (loading) {
    return (
      <section className="sd-page-stack sd-mqtt-page">
        <div className="sd-page-header">
          <div className="sd-heading">
            <div className="sd-eyebrow">MQTT OPERATIONS</div>
            <h1>MQTT Bridges</h1>
            <p>Loading broker connections, topic subscriptions, and external message history.</p>
          </div>
        </div>
        <div className="sd-panel sd-mqtt-loading">Loading MQTT bridge console...</div>
      </section>
    );
  }

  return (
    <section className="sd-page-stack sd-mqtt-page">
      <div className="sd-page-header sd-mqtt-header">
        <div className="sd-heading sd-mqtt-heading">
          <div className="sd-mqtt-title-row">
            <span className="sd-mqtt-title-icon" aria-hidden="true">
              <MqttIcon icon="bridge" />
            </span>
            <div>
              <div className="sd-eyebrow">MQTT OPERATIONS</div>
              <h1>MQTT Bridges</h1>
              <p>Connect external brokers, watch live topics, map devices, and publish commands.</p>
            </div>
          </div>
        </div>
        {isAdmin && (
          <div className="sd-page-actions">
            <button className="sd-secondary-button" onClick={() => openVirtualModal()} type="button">
              <span className="sd-mqtt-button-icon" aria-hidden="true">
                <MqttIcon icon="map" />
              </span>
              Virtual Device
            </button>
            <button className="sd-primary-button" onClick={startCreate} type="button">
              <span className="sd-mqtt-button-icon" aria-hidden="true">
                <MqttIcon icon="plus" />
              </span>
              New Bridge
            </button>
          </div>
        )}
      </div>

      <section className="sd-mqtt-flow-grid" aria-label="MQTT bridge workflow">
        {workflowSteps.map((step) => (
          <article className={`sd-mqtt-flow-card is-${step.accent}`} key={step.title}>
            <div className="sd-mqtt-flow-top">
              <span className="sd-mqtt-step-number">{step.number}</span>
              <span className="sd-mqtt-flow-icon" aria-hidden="true">
                <MqttIcon icon={step.icon} />
              </span>
            </div>
            <strong>{step.title}</strong>
            <p>{step.copy}</p>
            <span className="sd-mqtt-step-state">
              <i />
              {step.status}
            </span>
          </article>
        ))}
      </section>

      <div className="sd-mqtt-workspace">
        <aside className="sd-panel sd-mqtt-bridge-panel">
          <div className="sd-panel-header sd-mqtt-panel-header">
            <div>
              <h2>Broker Registry</h2>
              <p>{connections.length} external broker{connections.length === 1 ? '' : 's'} configured for this tenant.</p>
            </div>
            {isAdmin && (
              <button className="sd-mqtt-icon-command" onClick={startCreate} type="button" aria-label="Create MQTT bridge">
                <MqttIcon icon="plus" />
              </button>
            )}
          </div>

          {showForm && (
            <form className="sd-mqtt-connection-form" onSubmit={handleSubmit}>
              <div className="sd-mqtt-form-heading">
                <span className="sd-mqtt-form-icon" aria-hidden="true">
                  <MqttIcon icon="bridge" />
                </span>
                <div>
                  <strong>{editingId ? 'Edit Bridge' : 'Connect Bridge'}</strong>
                  <span>{editingId ? 'Update host, auth, or subscriptions.' : 'Create an external broker subscription.'}</span>
                </div>
              </div>
              <label className="sd-field">
                <span>Name</span>
                <input
                  placeholder="e.g. Factory Broker A"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
                <small>Friendly bridge name used in registries, message streams, and virtual device setup.</small>
              </label>
              <div className="sd-mqtt-form-grid">
                <label className="sd-field">
                  <span>Host</span>
                  <input
                    placeholder="e.g. broker.hivemq.com or 10.10.4.20"
                    value={form.host}
                    onChange={(event) => setForm({ ...form, host: event.target.value })}
                    required
                  />
                  <small>Broker DNS name or IP address without mqtt:// or port.</small>
                </label>
                <label className="sd-field">
                  <span>Port</span>
                  <input
                    placeholder="e.g. 1883"
                    type="number"
                    value={form.port}
                    onChange={(event) => setForm({ ...form, port: event.target.value })}
                  />
                  <small>MQTT listener port. Blank saves as 1883.</small>
                </label>
              </div>
              <label className="sd-field">
                <span>Username</span>
                <input
                  placeholder="Optional broker username"
                  value={form.username}
                  onChange={(event) => setForm({ ...form, username: event.target.value })}
                />
                <small>Leave blank for anonymous brokers or certificate-only access.</small>
              </label>
              <label className="sd-field sd-mqtt-password-field">
                <span>Password</span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={editingId ? 'Leave blank to keep current' : 'Optional'}
                  value={form.password}
                  onChange={(event) => setForm({ ...form, password: event.target.value })}
                />
                <small>{editingId ? 'Leave empty to keep the existing password.' : 'Optional secret for username/password brokers.'}</small>
                <button type="button" onClick={() => setShowPassword((visible) => !visible)}>
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </label>
              <label className="sd-field">
                <span>Topics</span>
                <input
                  placeholder="e.g. factory/sensors/#, alerts/+"
                  value={form.topics}
                  onChange={(event) => setForm({ ...form, topics: event.target.value })}
                  required
                />
                <small>Comma-separated MQTT subscriptions. Use # for multi-level and + for one level wildcards.</small>
              </label>
              <div className="sd-drawer-actions sd-mqtt-form-actions">
                <button type="button" onClick={cancelForm}>Cancel</button>
                <button type="submit">{editingId ? 'Save Changes' : 'Connect Bridge'}</button>
              </div>
            </form>
          )}

          <div className="sd-mqtt-connection-list">
            {connections.map((connection) => {
              const topics = parseTopics(connection.topics);
              const stats = connectionStats.get(connection.id) || { messageCount: 0, lastMessage: null };
              const isFocused = primaryConnection?.id === connection.id;
              return (
                <article
                  className={`sd-mqtt-connection-card ${connection.enabled ? 'is-enabled' : 'is-disabled'} ${isFocused ? 'is-selected' : ''}`}
                  key={connection.id}
                >
                  <div className="sd-mqtt-connection-top">
                    <span className="sd-mqtt-bridge-avatar" aria-hidden="true">
                      <MqttIcon icon="bridge" />
                    </span>
                    <div>
                      <strong>{connection.name}</strong>
                      <code>mqtt://{connection.host}:{connection.port}</code>
                    </div>
                    <div className="sd-mqtt-card-status">
                      <span className={`sd-status-label ${connection.enabled ? 'is-online' : 'is-offline'}`}>
                        <i />
                        {connection.enabled ? 'Connected' : 'Offline'}
                      </span>
                      <SignalBars active={connection.enabled} />
                    </div>
                  </div>
                  <div className="sd-mqtt-topic-strip">
                    {topics.map((topic) => (
                      <code key={topic}>{topic}</code>
                    ))}
                  </div>
                  <div className="sd-mqtt-connection-facts">
                    <div>
                      <span>Created</span>
                      <strong>{formatDate(connection.created_at)}</strong>
                    </div>
                    <div>
                      <span>Auth</span>
                      <strong>{connection.username || 'Anonymous'}</strong>
                    </div>
                    <div>
                      <span>Messages</span>
                      <strong>{stats.messageCount}</strong>
                    </div>
                  </div>
                  <div className="sd-mqtt-connection-facts is-secondary">
                    <div>
                      <span>Topics</span>
                      <strong>{topics.length}</strong>
                    </div>
                    <div>
                      <span>Last seen</span>
                      <strong>{stats.lastMessage ? formatRelativeTime(stats.lastMessage.received_at) : 'Waiting'}</strong>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="sd-row-actions sd-mqtt-card-actions">
                      <button onClick={() => startEdit(connection)} type="button">Edit</button>
                      <button onClick={() => handleToggle(connection.id, connection.enabled)} type="button">
                        {connection.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button className="is-danger" onClick={() => handleDelete(connection.id)} type="button">Delete</button>
                    </div>
                  )}
                </article>
              );
            })}
            {connections.length === 0 && (
              <div className="sd-mqtt-empty">
                <span className="sd-mqtt-empty-icon" aria-hidden="true">
                  <MqttIcon icon="plug" />
                </span>
                <strong>No MQTT bridges yet</strong>
                <span>Create a bridge to subscribe to external broker topics.</span>
              </div>
            )}
          </div>
        </aside>

        <article className="sd-panel sd-mqtt-explorer sd-mqtt-live-panel">
            <div className="sd-mqtt-explorer-header">
              <div>
                <h2>
                  <span className="sd-mqtt-section-icon" aria-hidden="true">
                    <MqttIcon icon="pulse" />
                  </span>
                  Live Payloads
                </h2>
                <p>
                  {messages.length} total
                  {selectedConnection ? ` from ${selectedConnection.name}` : ' across all bridges'}
                  {filteredMessages.length !== messages.length ? ` · ${filteredMessages.length} showing` : ''}
                </p>
              </div>
              <span className={`sd-live-pill ${paused ? 'is-stale' : 'is-live'}`}>
                <span />
                {paused ? 'PAUSED' : 'LIVE'}
              </span>
            </div>

            <div className="sd-mqtt-toolbar">
              <label className="sd-mqtt-search-field">
                <span aria-hidden="true">
                  <MqttIcon icon="search" />
                </span>
                <input
                  placeholder="Search topics or payloads..."
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                />
              </label>
              <select value={filterConn} onChange={(event) => setFilterConn(event.target.value)}>
                <option value="all">All topics</option>
                {connections.map((connection) => (
                  <option key={connection.id} value={connection.id}>
                    {connection.name}
                  </option>
                ))}
              </select>
              <button className={paused ? 'is-success' : 'is-warning'} onClick={() => setPaused((value) => !value)} type="button">
                <span className="sd-mqtt-button-icon" aria-hidden="true">
                  <MqttIcon icon={paused ? 'play' : 'pause'} />
                </span>
                {paused ? 'Resume' : 'Pause'}
              </button>
              <button className={viewMode === 'tree' ? 'is-success' : ''} onClick={() => setViewMode((value) => (value === 'list' ? 'tree' : 'list'))} type="button">
                <span className="sd-mqtt-button-icon" aria-hidden="true">
                  <MqttIcon icon="filter" />
                </span>
                {viewMode === 'list' ? 'Tree' : 'List'}
              </button>
              <button onClick={handleExport} type="button">Export</button>
              <button className="is-danger" onClick={handleClear} type="button">Clear</button>
            </div>

            <div className="sd-mqtt-message-list" ref={msgListRef}>
              {viewMode === 'list' ? (
                <div className="sd-mqtt-payload-timeline">
                  {filteredMessages.map((message, index) => (
                    <article className="sd-mqtt-message-row" key={`${message.received_at}-${index}`} onClick={() => setDetailMsg(message)}>
                      <span className="sd-mqtt-timeline-dot" aria-hidden="true" />
                      <div className="sd-mqtt-message-body">
                        <header>
                          <time>{formatTime(message.received_at)}</time>
                          <code>{message.topic}</code>
                          <span>{message.connection_name}</span>
                          <button
                            aria-label="Copy payload"
                            onClick={(event) => {
                              event.stopPropagation();
                              copyPayload(message.payload, index);
                            }}
                            type="button"
                          >
                            <MqttIcon icon="copy" />
                            {copiedIdx === index ? 'Copied' : 'Copy'}
                          </button>
                        </header>
                        <pre>{tryFormatJson(message.payload)}</pre>
                        {message.payload.length > 160 && <small>Open detail view for the full payload.</small>}
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="sd-mqtt-tree-list">
                  {Object.entries(topicTree).map(([root, topicMessages]) => (
                    <div className="sd-mqtt-tree-group" key={root}>
                      <div className="sd-mqtt-tree-heading">
                        <strong>{root}</strong>
                        <span>{pluralize(topicMessages.length, 'message')}</span>
                      </div>
                      {topicMessages.slice(0, 10).map((message, index) => (
                        <button className="sd-mqtt-tree-item" key={`${message.received_at}-${index}`} onClick={() => setDetailMsg(message)} type="button">
                          <code>{message.topic}</code>
                          <span>{message.payload.substring(0, 80)}{message.payload.length > 80 ? '...' : ''}</span>
                        </button>
                      ))}
                      {topicMessages.length > 10 && <em>+ {topicMessages.length - 10} more in this topic root</em>}
                    </div>
                  ))}
                </div>
              )}
              {filteredMessages.length === 0 && (
                <div className="sd-mqtt-empty">
                  <span className="sd-mqtt-empty-icon" aria-hidden="true">
                    <MqttIcon icon="pulse" />
                  </span>
                  <strong>{messages.length === 0 ? 'Waiting for messages' : 'No messages match this filter'}</strong>
                  <span>{messages.length === 0 ? 'Live external MQTT payloads will appear here.' : 'Adjust the broker filter or search text.'}</span>
                </div>
              )}
            </div>

            <div className="sd-mqtt-stream-footer">
              <span>
                {filteredMessages.length > 0
                  ? `Showing latest ${pluralize(filteredMessages.length, 'payload')}`
                  : `${liveTopicRootCount} live root${liveTopicRootCount === 1 ? '' : 's'}`}
              </span>
              <button className={autoScroll ? 'is-on' : ''} onClick={() => setAutoScroll((value) => !value)} type="button">
                <i />
                {autoScroll ? 'Auto scroll' : 'Manual scroll'}
              </button>
            </div>
          </article>

        <aside className="sd-mqtt-action-rail">
          {isAdmin ? (
            <form className="sd-panel sd-mqtt-publisher" onSubmit={handlePublish}>
              <div className="sd-mqtt-publisher-header">
                <div>
                  <h2>
                    <span className="sd-mqtt-section-icon is-violet" aria-hidden="true">
                      <MqttIcon icon="send" />
                    </span>
                    Publish Command
                  </h2>
                  <p>Send a command to a topic and monitor the response.</p>
                </div>
                <button
                  className={`sd-mqtt-retain-toggle ${pubRetain ? 'is-retained' : ''}`}
                  onClick={() => setPubRetain((value) => !value)}
                  type="button"
                >
                  <i />
                  Retain {pubRetain ? 'On' : 'Off'}
                </button>
              </div>
              <div className="sd-mqtt-publisher-fields">
                <label className="sd-field">
                  <span>Bridge</span>
                  <select value={pubConn} onChange={(event) => setPubConn(event.target.value)} required disabled={enabledConnectionOptions.length === 0}>
                    {enabledConnectionOptions.length === 0 && <option value="">No enabled bridge</option>}
                    {enabledConnectionOptions.map((connection) => (
                      <option key={connection.id} value={connection.id}>
                        {connection.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="sd-field">
                  <span>Topic</span>
                  <input
                    placeholder="factory/line-a/press/cmd"
                    value={pubTopic}
                    onChange={(event) => setPubTopic(event.target.value)}
                    required
                  />
                </label>
                <label className="sd-field">
                  <span>Payload</span>
                  <textarea
                    placeholder={pubFormat === 'json' ? '{"command": "start", "speed": 1200}' : pubFormat === 'hex' ? '48656c6c6f' : 'Hello world'}
                    value={pubPayload}
                    onChange={(event) => setPubPayload(event.target.value)}
                    rows={4}
                  />
                </label>
                <div className="sd-mqtt-publisher-grid">
                  <label className="sd-field">
                    <span>Format</span>
                    <select value={pubFormat} onChange={(event) => setPubFormat(event.target.value as any)}>
                      <option value="plaintext">Plaintext</option>
                      <option value="json">JSON</option>
                      <option value="hex">Hex</option>
                      <option value="base64">Base64</option>
                    </select>
                  </label>
                  <label className="sd-field">
                    <span>QoS</span>
                    <select value={pubQos} onChange={(event) => setPubQos(Number(event.target.value))}>
                      <option value={0}>QoS 0</option>
                      <option value={1}>QoS 1</option>
                      <option value={2}>QoS 2</option>
                    </select>
                  </label>
                </div>
                <button className="sd-mqtt-publish-button" disabled={pubSending || enabledConnectionOptions.length === 0} type="submit">
                  <MqttIcon icon="send" />
                  {pubSent ? 'Sent' : pubSending ? 'Sending...' : 'Publish Command'}
                </button>
              </div>
            </form>
          ) : (
            <article className="sd-panel sd-mqtt-readonly-card">
              <span className="sd-mqtt-section-icon is-violet" aria-hidden="true">
                <MqttIcon icon="send" />
              </span>
              <strong>Publish Command</strong>
              <p>Ask an admin to publish control payloads through enabled MQTT bridges.</p>
            </article>
          )}

          <article className="sd-panel sd-mqtt-mapper-card">
            <div className="sd-mqtt-publisher-header">
              <div>
                <h2>
                  <span className="sd-mqtt-section-icon is-amber" aria-hidden="true">
                    <MqttIcon icon="map" />
                  </span>
                  Virtual Mapper
                </h2>
                <p>Create a virtual device from a bridge topic and JSON path.</p>
              </div>
            </div>
            <div className="sd-mqtt-mapper-body">
              <div className="sd-mqtt-mapper-summary">
                <SummaryFact label="Selected" value={primaryConnection?.name || 'No bridge'} />
                <SummaryFact label="Topics" value={String(primaryTopics.length)} />
                <SummaryFact label="Messages" value={String(primaryStats.messageCount)} />
              </div>
              <div className="sd-mqtt-topic-strip">
                {primaryTopics.slice(0, 3).map((topic) => (
                  <code key={topic}>{topic}</code>
                ))}
                {primaryTopics.length > 3 && <code>+{primaryTopics.length - 3} more</code>}
              </div>
              {isAdmin ? (
                <button
                  className="sd-secondary-button"
                  disabled={!primaryConnection}
                  onClick={() => openVirtualModal(undefined, primaryConnection?.id)}
                  type="button"
                >
                  <span className="sd-mqtt-button-icon" aria-hidden="true">
                    <MqttIcon icon="map" />
                  </span>
                  Open Mapper
                </button>
              ) : (
                <p className="sd-form-hint">Admins can map external payloads into tenant devices.</p>
              )}
            </div>
          </article>
        </aside>
      </div>

      {detailMsg && (
        <div className="sd-mqtt-modal-overlay" onClick={() => setDetailMsg(null)}>
          <div className="sd-mqtt-modal" onClick={(event) => event.stopPropagation()}>
            <div className="sd-mqtt-modal-header">
              <div>
                <span>Message Detail</span>
                <h2>{detailMsg.connection_name}</h2>
              </div>
              <button onClick={() => setDetailMsg(null)} type="button">Close</button>
            </div>
            <div className="sd-mqtt-modal-body">
              <div className="sd-mqtt-detail-grid">
                <SummaryFact label="Topic" value={detailMsg.topic} code />
                <SummaryFact label="Received" value={new Date(detailMsg.received_at).toLocaleString()} />
                <SummaryFact label="Connection ID" value={detailMsg.connection_id} code />
              </div>
              <pre className="sd-mqtt-modal-payload">{tryFormatJson(detailMsg.payload)}</pre>
              <div className="sd-mqtt-modal-actions">
                <button
                  className="sd-secondary-button"
                  onClick={async () => {
                    const ok = await copyToClipboard(detailMsg.payload);
                    if (ok) {
                      setCopiedIdx(-1);
                      setTimeout(() => setCopiedIdx(null), 1200);
                    }
                  }}
                  type="button"
                >
                  {copiedIdx === -1 ? 'Copied' : 'Copy Payload'}
                </button>
                {isAdmin && (
                  <button
                    className="sd-primary-button"
                    onClick={() => {
                      setDetailMsg(null);
                      openVirtualModal(detailMsg);
                    }}
                    type="button"
                  >
                    Create Virtual Device
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showVirtualModal && (
        <div className="sd-mqtt-modal-overlay" onClick={() => setShowVirtualModal(false)}>
          <div className="sd-mqtt-modal sd-mqtt-modal-wide" onClick={(event) => event.stopPropagation()}>
            <div className="sd-mqtt-modal-header">
              <div>
                <span>Virtual Asset Mapper</span>
                <h2>Create Virtual Device</h2>
              </div>
              <button onClick={() => setShowVirtualModal(false)} type="button">Close</button>
            </div>
            <div className="sd-mqtt-modal-body">
              <div className="sd-mqtt-virtual-grid">
                <label className="sd-field">
                  <span>Device Name</span>
                  <input
                    placeholder="e.g. HAT-1 Temperature Sensor"
                    value={vdName}
                    onChange={(event) => setVdName(event.target.value)}
                  />
                  <small>Name for the virtual device created from this broker stream.</small>
                </label>
                <label className="sd-field">
                  <span>Bridge</span>
                  <select value={vdConn} onChange={(event) => setVdConn(event.target.value)}>
                    {connections.map((connection) => (
                      <option key={connection.id} value={connection.id}>
                        {connection.name}
                      </option>
                    ))}
                  </select>
                  <small>Broker connection that receives this virtual device's payloads.</small>
                </label>
                <label className="sd-field sd-mqtt-virtual-topic">
                  <span>Topic Pattern</span>
                  <input
                    placeholder="e.g. factory/sensors/hat1 or plant/+/meter"
                    value={vdTopicPattern}
                    onChange={(event) => setVdTopicPattern(event.target.value)}
                  />
                  <small>Topic or wildcard pattern used to recognize this virtual device.</small>
                </label>
              </div>

              <div className="sd-mqtt-section-title">Field Mappings</div>
              <div className="sd-mqtt-mapping-stack">
                {vdMappings.map((mapping, index) => (
                  <div className="sd-mqtt-mapping-card" key={mapping.rowId}>
                    <div className="sd-mqtt-mapping-grid">
                      <label className="sd-field">
                        <span>Field Key</span>
                        <input
                          placeholder="e.g. fuel_level"
                          value={mapping.field}
                          onChange={(event) => updateMapping(index, { field: event.target.value })}
                        />
                        <small>Internal metric key. Use short snake_case names.</small>
                      </label>
                      <label className="sd-field">
                        <span>Display Label</span>
                        <input
                          placeholder="e.g. Fuel Level"
                          value={mapping.label}
                          onChange={(event) => updateMapping(index, { label: event.target.value })}
                        />
                        <small>Operator-facing label used in dashboards and live readings.</small>
                      </label>
                      <label className="sd-field">
                        <span>Type</span>
                        <select
                          value={mapping.type}
                          onChange={(event) => updateMapping(index, { type: event.target.value as 'number' | 'string' })}
                        >
                          <option value="number">Number</option>
                          <option value="string">String</option>
                        </select>
                        <small>Use String for states like ON/OFF or RUNNING/FAULT.</small>
                      </label>
                      <label className="sd-field sd-mqtt-path-field">
                        <span>JSON Path</span>
                        <input
                          placeholder="e.g. $.fuel_litres or data.level"
                          value={mapping.path}
                          onChange={(event) => updateMapping(index, { path: event.target.value })}
                        />
                        <small>Path to the value inside the raw JSON payload.</small>
                      </label>
                      <label className="sd-field">
                        <span>Scale</span>
                        <input
                          disabled={mapping.type === 'string'}
                          placeholder="e.g. 1"
                          step="any"
                          type="number"
                          value={mapping.scale}
                          onChange={(event) => updateMapping(index, { scale: event.target.value })}
                        />
                        <small>Multiplier for numeric values. Blank defaults to 1.</small>
                      </label>
                      <label className="sd-field">
                        <span>Offset</span>
                        <input
                          disabled={mapping.type === 'string'}
                          placeholder="e.g. 0"
                          step="any"
                          type="number"
                          value={mapping.offset}
                          onChange={(event) => updateMapping(index, { offset: event.target.value })}
                        />
                        <small>Calibration value added after scale. Blank defaults to 0.</small>
                      </label>
                      <label className="sd-field">
                        <span>Unit</span>
                        <input
                          placeholder="e.g. L, %, bar, kWh"
                          value={mapping.unit}
                          onChange={(event) => updateMapping(index, { unit: event.target.value })}
                        />
                        <small>Optional engineering unit shown beside the mapped value.</small>
                      </label>
                    </div>
                    <button
                      className="sd-mqtt-remove-field"
                      onClick={() =>
                        setVdMappings((current) => current.filter((_, mappingIndex) => mappingIndex !== index))
                      }
                      type="button"
                    >
                      Remove Field
                    </button>
                  </div>
                ))}
              </div>
              <button
                className="sd-secondary-button"
                onClick={() =>
                  setVdMappings((current) => [
                    ...current,
                    createDefaultMapping({ field: '', label: '', unit: '' }),
                  ])
                }
                type="button"
              >
                Add Field
              </button>

              <div className="sd-mqtt-section-title">Test Sandbox</div>
              <div className="sd-mqtt-test-grid">
                <textarea
                  placeholder='Paste raw JSON, e.g. {"fuel_litres": 1230.2, "state": "ON"}'
                  value={vdTestPayload}
                  onChange={(event) => setVdTestPayload(event.target.value)}
                  rows={6}
                />
                <div className="sd-mqtt-test-result">
                  <button className="sd-primary-button" onClick={runPreview} type="button">Test Parser</button>
                  {vdPreview && <pre>{JSON.stringify(vdPreview, null, 2)}</pre>}
                  {vdPreview === null && vdTestPayload && <span>No fields matched. Check the JSON paths.</span>}
                </div>
              </div>

              <div className="sd-mqtt-modal-actions">
                <button className="sd-secondary-button" onClick={() => setShowVirtualModal(false)} type="button">Cancel</button>
                <button
                  className="sd-primary-button"
                  disabled={!vdName || !vdConn || !vdTopicPattern}
                  onClick={saveVirtualDevice}
                  type="button"
                >
                  Create Device
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function SummaryFact({ code, label, value }: { code?: boolean; label: string; value: string }) {
  return (
    <div className="sd-mqtt-summary-fact">
      <span>{label}</span>
      {code ? <code>{value}</code> : <strong>{value}</strong>}
    </div>
  );
}

function SignalBars({ active }: { active: boolean }) {
  return (
    <span className={`sd-mqtt-signal-bars ${active ? 'is-active' : ''}`} aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  );
}

function MqttIcon({ icon }: { icon: MqttIconName }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 2,
  };

  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      {icon === 'bridge' && (
        <>
          <path {...common} d="M10 13a5 5 0 0 0 7.1 0l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1" />
          <path {...common} d="M14 11a5 5 0 0 0-7.1 0l-2 2A5 5 0 0 0 12 20.1l1.1-1.1" />
        </>
      )}
      {icon === 'plug' && (
        <>
          <path {...common} d="M8 3v5" />
          <path {...common} d="M16 3v5" />
          <path {...common} d="M7 8h10v4a5 5 0 0 1-10 0V8Z" />
          <path {...common} d="M12 17v4" />
        </>
      )}
      {icon === 'pulse' && (
        <>
          <path {...common} d="M3 12h4l2-6 4 12 2-6h6" />
          <path {...common} d="M4 18h16" opacity="0.35" />
        </>
      )}
      {icon === 'map' && (
        <>
          <rect {...common} x="4" y="5" width="16" height="14" rx="2" />
          <path {...common} d="M8 9h4" />
          <path {...common} d="M8 13h8" />
          <path {...common} d="M14 9h2" />
        </>
      )}
      {icon === 'send' && (
        <>
          <path {...common} d="m21 3-7 18-4-8-7-4 18-6Z" />
          <path {...common} d="m10 13 4-4" />
        </>
      )}
      {icon === 'plus' && (
        <>
          <path {...common} d="M12 5v14" />
          <path {...common} d="M5 12h14" />
        </>
      )}
      {icon === 'pause' && (
        <>
          <path {...common} d="M8 5v14" />
          <path {...common} d="M16 5v14" />
        </>
      )}
      {icon === 'play' && <path {...common} d="m8 5 11 7-11 7V5Z" />}
      {icon === 'filter' && (
        <>
          <path {...common} d="M4 6h16" />
          <path {...common} d="M7 12h10" />
          <path {...common} d="M10 18h4" />
        </>
      )}
      {icon === 'copy' && (
        <>
          <rect {...common} x="8" y="8" width="11" height="11" rx="2" />
          <path {...common} d="M5 15H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1" />
        </>
      )}
      {icon === 'clock' && (
        <>
          <circle {...common} cx="12" cy="12" r="8" />
          <path {...common} d="M12 8v5l3 2" />
        </>
      )}
      {icon === 'calendar' && (
        <>
          <rect {...common} x="4" y="5" width="16" height="15" rx="2" />
          <path {...common} d="M8 3v4" />
          <path {...common} d="M16 3v4" />
          <path {...common} d="M4 10h16" />
        </>
      )}
      {icon === 'lock' && (
        <>
          <rect {...common} x="5" y="10" width="14" height="10" rx="2" />
          <path {...common} d="M8 10V8a4 4 0 0 1 8 0v2" />
        </>
      )}
      {icon === 'database' && (
        <>
          <ellipse {...common} cx="12" cy="6" rx="7" ry="3" />
          <path {...common} d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6" />
          <path {...common} d="M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" />
        </>
      )}
      {icon === 'search' && (
        <>
          <circle {...common} cx="11" cy="11" r="6" />
          <path {...common} d="m16 16 4 4" />
        </>
      )}
    </svg>
  );
}

function formatDate(value: string) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString();
}

function formatTime(value: string) {
  if (!value) return '-';
  return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatRelativeTime(value: string) {
  if (!value) return '-';
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
